import { useState, useEffect, useCallback, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWandMagicSparkles,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import type { DevToolsPlugin } from "../../types";
import { FilterableList } from "../../FilterableList";
import type { FilterableSection } from "../../FilterableList";

const TAG_COLORS = [
  "#1a6b3f",
  "#6b3fa0",
  "#2563eb",
  "#b45309",
  "#be185d",
  "#0e7490",
];

const ACRONYMS = new Set(["usa", "uk", "qcp", "gl", "bop", "wc", "pl"]);

interface WarpPreset {
  id: string;
  label: string;
  journey_name: string;
  step: string;
  vertical: string;
  tags: string[];
}

function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || ""
  );
}

function humanize(name: string): string {
  return name
    .split("_")
    .map((word) =>
      ACRONYMS.has(word)
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
}

function badges(preset: WarpPreset): string[] {
  return [humanize(preset.vertical), ...preset.tags.slice(1)];
}

function groupBy<T>(items: T[], keyOf: (item: T) => string): Record<string, T[]> {
  const grouped: Record<string, T[]> = {};
  for (const item of items) {
    const key = keyOf(item);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  }
  return grouped;
}

function WarpToStepPanel() {
  const [presets, setPresets] = useState<Record<string, WarpPreset[]> | null>(null);
  const [warping, setWarping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/dev_tools/journey/warp_targets")
      .then((res) => res.json())
      .then((data) => {
        setPresets(
          groupBy(data.presets || [], (p: WarpPreset) => p.journey_name || "other"),
        );
      })
      .catch(() => setError("Failed to load warp targets"));
  }, []);

  const handleWarp = useCallback((presetId: string) => {
    setWarping(true);
    setError(null);

    fetch("/dev_tools/journey/warp_to_step", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify({ preset_id: presetId }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.url) {
          window.location.href = data.url;
        } else {
          setError(data.error || "Unknown error");
          setWarping(false);
        }
      })
      .catch((err) => {
        setError(`Request failed: ${err.message}`);
        setWarping(false);
      });
  }, []);

  const renderChips = useCallback(
    (items: WarpPreset[]) => (
      <div className="dt-chip-grid">
        {items.map((preset) => (
          <button
            key={preset.id}
            className="dt-chip dt-warp__chip"
            disabled={warping}
            onClick={() => handleWarp(preset.id)}
            type="button"
          >
            <span>{preset.label}</span>
            <span className="dt-warp__tags">
              {badges(preset).map((badge, i) => (
                <span
                  key={badge}
                  className="dt-warp__tag"
                  style={{ background: TAG_COLORS[i % TAG_COLORS.length] }}
                >
                  {badge}
                </span>
              ))}
            </span>
          </button>
        ))}
      </div>
    ),
    [warping, handleWarp],
  );

  const renderSteps = useCallback(
    (items: WarpPreset[]) =>
      Object.entries(groupBy(items, (p) => p.step)).map(([step, stepItems]) => (
        <div key={step} className="dt-warp__step">
          <div className="dt-section-label">{humanize(step)}</div>
          {renderChips(stepItems)}
        </div>
      )),
    [renderChips],
  );

  const sections: FilterableSection<WarpPreset>[] = useMemo(
    () =>
      Object.entries(presets || {}).map(([journeyName, items]) => ({
        key: journeyName,
        title: humanize(journeyName),
        items,
        matchText: (p: WarpPreset) =>
          `${p.label} ${humanize(p.step)} ${p.vertical} ${badges(p).join(" ")}`,
        renderItems: renderSteps,
      })),
    [presets, renderSteps],
  );

  if (error) {
    return (
      <div className="dt-status-banner dt-status-banner--warning">
        <div className="dt-status-banner__title">Error</div>
        <div className="dt-status-banner__text">{error}</div>
      </div>
    );
  }

  if (presets === null) {
    return (
      <div className="dt-skip__description">
        <FontAwesomeIcon icon={faSpinner} spin /> Loading warp targets...
      </div>
    );
  }

  return (
    <>
      <div className="dt-skip__description">
        Jump directly to a journey step with pre-populated data, bypassing all
        preceding steps.
      </div>

      <FilterableList sections={sections} placeholder="Filter presets..." />

      {warping && (
        <div
          className="dt-autofill__overlay"
          aria-busy="true"
          aria-live="polite"
        >
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className="dt-autofill__spinner"
          />
        </div>
      )}
    </>
  );
}

export function warpToStepPlugin(): DevToolsPlugin {
  return {
    id: "warp-to-step",
    name: "Warp to Step",
    icon: faWandMagicSparkles,
    render: () => <WarpToStepPanel />,
  };
}
